/**
 * FeatureDemos — looping, self-playing product mockups for the landing page
 * (Notion-style "video" sections, built as animated UI instead of mp4s).
 * Each scene advances through timed steps and loops forever; elements re-mount
 * on each loop so their entrance animations replay.
 */
import React, { useEffect, useState } from 'react';

/** Steps 0..totalSteps-1, advancing every stepMs, looping forever. */
const useLoopStep = (totalSteps: number, stepMs: number) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % totalSteps), stepMs);
    return () => clearInterval(id);
  }, [totalSteps, stepMs]);
  return step;
};

/** Browser-style window chrome shared by all demo scenes. */
export const DemoWindow: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({
  title,
  children,
  className = '',
}) => (
  <div
    className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-large dark:border-secondary-700 dark:bg-secondary-800 ${className}`}
  >
    <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-2.5 dark:border-secondary-700 dark:bg-secondary-900">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
      <span className="ml-3 truncate text-xs font-medium text-gray-500 dark:text-gray-400">{title}</span>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const TypingDots: React.FC = () => (
  <span className="inline-flex items-end gap-1 px-1 py-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-gray-400"
        style={{ animation: `demoTypingDot 1s ease-in-out ${i * 0.15}s infinite` }}
      />
    ))}
  </span>
);

/* ------------------------------------------------------------------ */
/* 1. Upload a lecture → topics extracted → tutor ready                */
/* ------------------------------------------------------------------ */

const TOPICS = ['Light reactions', 'Calvin cycle', 'Chlorophyll', 'ATP synthesis'];

const PHASES = [
  { key: 'topics', label: 'Topics' },
  { key: 'tutor', label: 'AI tutor' },
  { key: 'quiz', label: 'Quiz' },
];

export const UploadDemo: React.FC = () => {
  const step = useLoopStep(15, 950);
  const phase = step < 6 ? 0 : step < 10 ? 1 : 2;

  return (
    <DemoWindow title="classrooms.work / Biology 101 / Week 4 - Photosynthesis">
      <div className="flex min-h-[250px] flex-col gap-3">
        {/* Story stepper: one lecture becomes topics, a tutor, and a quiz */}
        <div className="flex items-center gap-2">
          {PHASES.map((p, i) => (
            <React.Fragment key={p.key}>
              {i > 0 && <span className="h-px w-4 bg-gray-200 dark:bg-secondary-600" />}
              <span
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold transition-all duration-300 ${
                  i === phase
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300'
                    : i < phase
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                      : 'bg-gray-100 text-gray-400 dark:bg-secondary-700 dark:text-gray-500'
                }`}
              >
                {i < phase ? '\u2713' : i + 1} {p.label}
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* Act 1: upload + topic extraction */}
        {phase === 0 && (
          <div key="topics" className="demo-pop flex flex-1 flex-col gap-3">
            <div className="flex items-center gap-3 rounded-xl border-2 border-dashed border-gray-200 p-3 dark:border-secondary-600">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100 text-lg dark:bg-violet-900/40">🎬</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">Week 4 - Photosynthesis.mp4</p>
                {step < 1 && (
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                    <div className="demo-bar h-full w-3/4 rounded-full bg-primary-500" style={{ animationDuration: '1.4s' }} />
                  </div>
                )}
                {step >= 1 && <p className="demo-pop mt-1 text-xs font-medium text-primary-600">Extracting topics with AI...</p>}
              </div>
            </div>
            <div className="flex min-h-[56px] flex-wrap content-start gap-2">
              {TOPICS.map(
                (topic, i) =>
                  step >= 2 + i && (
                    <span
                      key={topic}
                      className="demo-pop rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700 dark:border-secondary-600 dark:bg-secondary-700 dark:text-gray-200"
                    >
                      {topic}
                    </span>
                  ),
              )}
            </div>
          </div>
        )}

        {/* Act 2: the tutor answers from this same lecture */}
        {phase === 1 && (
          <div key="tutor" className="demo-pop flex flex-1 flex-col justify-end gap-2.5">
            <div className="demo-pop self-end rounded-2xl rounded-br-md bg-primary-600 px-3.5 py-2 text-xs font-medium text-gray-900 shadow-soft">
              What does the Calvin cycle actually produce?
            </div>
            {step === 7 && (
              <div className="demo-pop self-start rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 dark:bg-secondary-700">
                <TypingDots />
              </div>
            )}
            {step >= 8 && (
              <div className="demo-pop max-w-[88%] self-start rounded-2xl rounded-bl-md bg-gray-100 px-3.5 py-2 text-xs text-gray-700 shadow-soft dark:bg-secondary-700 dark:text-gray-200">
                It uses the ATP and NADPH from the light reactions to build G3P, the sugar precursor.
                <span className="demo-pop mt-1.5 flex w-fit items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-primary-600 shadow-soft dark:bg-secondary-800">
                  📎 From this lecture: 12:35
                </span>
              </div>
            )}
          </div>
        )}

        {/* Act 3: a quiz generated from the same content */}
        {phase === 2 && (
          <div key="quiz" className="demo-pop flex flex-1 flex-col gap-2">
            {step === 10 && (
              <p className="demo-pop text-xs font-medium text-primary-600">Generating quiz from this lecture...</p>
            )}
            {step >= 11 && (
              <div className="demo-pop rounded-xl border border-gray-200 p-2.5 dark:border-secondary-600">
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: '#1A2744' }}>Remember</span>
                <p className="mt-1 text-xs font-semibold text-gray-800 dark:text-gray-100">Where do the light reactions take place?</p>
              </div>
            )}
            {step >= 12 && (
              <div className="demo-pop rounded-xl border border-gray-200 p-2.5 dark:border-secondary-600">
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: '#1A7A3E' }}>Apply</span>
                <p className="mt-1 text-xs font-semibold text-gray-800 dark:text-gray-100">Predict what happens to G3P output if ATP runs low.</p>
              </div>
            )}
            {step >= 13 && (
              <div className="demo-pop flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">
                <span>🎉</span> One lecture became 4 topics, an AI tutor, and a 6-question quiz.
              </div>
            )}
          </div>
        )}
      </div>
    </DemoWindow>
  );
};

/* ------------------------------------------------------------------ */
/* 2. AI tutor chat grounded in the lecture                            */
/* ------------------------------------------------------------------ */

export const TutorDemo: React.FC = () => {
  const step = useLoopStep(8, 950);
  return (
    <DemoWindow title="classrooms.work / Photosynthesis / Ask the tutor">
      <div className="flex min-h-[220px] flex-col justify-end gap-3">
        {step >= 1 && (
          <div className="demo-pop self-end rounded-2xl rounded-br-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-gray-900 shadow-soft">
            Why do leaves look green?
          </div>
        )}
        {step >= 2 && step < 4 && (
          <div className="demo-pop self-start rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 dark:bg-secondary-700">
            <TypingDots />
          </div>
        )}
        {step >= 4 && (
          <div className="demo-pop max-w-[85%] self-start rounded-2xl rounded-bl-md bg-gray-100 px-4 py-2.5 text-sm text-gray-700 shadow-soft dark:bg-secondary-700 dark:text-gray-200">
            Chlorophyll absorbs red and blue light for the light reactions, but it reflects green light, so
            that's the color that reaches your eyes.
            {step >= 5 && (
              <span className="demo-pop mt-2 flex w-fit items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-primary-600 shadow-soft dark:bg-secondary-800">
                📎 From your lecture: Week 4, 12:35
              </span>
            )}
          </div>
        )}
        {step >= 6 && (
          <div className="demo-pop self-end rounded-2xl rounded-br-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-gray-900 shadow-soft">
            Quiz me on that!
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2.5 dark:border-secondary-600">
          <span className="flex-1 text-sm text-gray-500">Ask anything about this lecture...</span>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-xs text-gray-900">↑</span>
        </div>
      </div>
    </DemoWindow>
  );
};

/* ------------------------------------------------------------------ */
/* 3. Bloom's taxonomy quizzes                                         */
/* ------------------------------------------------------------------ */

const QUIZ_OPTIONS = [
  { text: 'It would grow faster than before', correct: false },
  { text: 'Photosynthesis would slow dramatically', correct: true },
  { text: 'The leaves would turn blue', correct: false },
];

export const QuizDemo: React.FC = () => {
  const step = useLoopStep(8, 900);
  return (
    <DemoWindow title="classrooms.work / Photosynthesis / Practice">
      <div className="flex min-h-[220px] flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: '#1A7A3E' }}>
            Bloom's level: Apply
          </span>
          <span className="text-xs font-semibold text-gray-500">Question 3 of 6</span>
        </div>
        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
          A plant is placed under a pure green light. Predict what happens.
        </p>
        <div className="flex flex-col gap-2">
          {QUIZ_OPTIONS.map((o, i) => {
            const picked = step >= 3 && o.correct;
            return (
              step >= 1 && (
                <div
                  key={i}
                  className={`demo-pop rounded-xl border-2 px-3.5 py-2.5 text-sm font-medium transition-all duration-300 ${
                    picked
                      ? 'border-green-500 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'border-gray-200 text-gray-600 dark:border-secondary-600 dark:text-gray-300'
                  }`}
                  style={{ animationDelay: `${i * 0.12}s` }}
                >
                  {picked ? '✓ ' : ''}
                  {o.text}
                </div>
              )
            );
          })}
        </div>
        {step >= 5 && (
          <div className="demo-pop flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            <span>💡</span> Exactly. Chlorophyll reflects green light, so the plant can barely use it. One step
            closer to mastering Apply.
          </div>
        )}
      </div>
    </DemoWindow>
  );
};

/* ------------------------------------------------------------------ */
/* 4. Teacher insight: which topics the class is struggling with       */
/* ------------------------------------------------------------------ */

const HARD_TOPICS: {
  name: string;
  pct: number; // class mastery
  stuck: number; // students struggling
  tone: 'hard' | 'warm' | 'ok';
}[] = [
  { name: 'Calvin cycle', pct: 38, stuck: 14, tone: 'hard' },
  { name: 'ATP synthesis', pct: 55, stuck: 9, tone: 'warm' },
  { name: 'Light reactions', pct: 82, stuck: 3, tone: 'ok' },
  { name: 'Chlorophyll', pct: 91, stuck: 1, tone: 'ok' },
];

const TONE_STYLES = {
  hard: { bar: '#ef4444', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  warm: { bar: '#f59e0b', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  ok: { bar: '#22c55e', badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
};

export const InsightsDemo: React.FC = () => {
  const step = useLoopStep(10, 850);
  return (
    <DemoWindow title="classrooms.work / Biology 101 / Class insights">
      <div className="flex min-h-[220px] flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Challenging topics this week</p>
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-300">
            32 students
          </span>
        </div>
        {HARD_TOPICS.map(
          (t, i) =>
            step >= 1 + i && (
              <div
                key={t.name}
                className="demo-pop flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2 dark:border-secondary-600"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-xs font-bold text-gray-700 dark:text-gray-200">{t.name}</p>
                    {t.tone === 'hard' && (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE_STYLES.hard.badge}`}>
                        Hardest topic
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                    <div
                      className="demo-bar h-full rounded-full"
                      style={{ width: `${t.pct}%`, backgroundColor: TONE_STYLES[t.tone].bar, animationDelay: '0.15s' }}
                    />
                  </div>
                </div>
                <div className="flex w-24 flex-shrink-0 flex-col items-end">
                  <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{t.pct}% mastery</span>
                  <span className="text-[11px] text-gray-500">{t.stuck} students stuck</span>
                </div>
              </div>
            ),
        )}
        {step >= 6 && (
          <div className="demo-pop mt-1 flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
            <span>💡</span>
            <span className="flex-1">
              14 students are stuck on the Calvin cycle at the Understand level. Assign a review quiz?
            </span>
            {step >= 8 && (
              <span className="demo-pop flex-shrink-0 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-bold text-white">
                Assign review
              </span>
            )}
          </div>
        )}
      </div>
    </DemoWindow>
  );
};
